import {useEffect} from "react";
import {useLocation} from 'react-router';
import {useParams} from 'react-router-dom';
import {Helmet, HelmetProvider} from "react-helmet-async";
import {useConstants, useFieldOptions, useOrganizationSettings} from 'api';
import {SettingsContext, useUnionTranslator} from './application/utils';
import DataErrorFallback from '@super-template/core/src/DataErrorFallback';
import {NumberParam, StringParam, withDefault} from 'use-query-params';
import useSessionStorage from '@postinumero/storage/useSessionStorage';
import OrganizationTheme from "@super-template/theme/src/OrganizationTheme";
import {ThemeProvider} from '@material-ui/core/styles';
import useLocale from "@super-template/core/src/locale/useLocale";
import messages from "@super-template/messages";
import {useIntl} from "react-intl";
import remove from "lodash/remove"

const prefix = 'PUBLIC_INFO_';

export const UseOptions = ({children}) => {
  const intl = useIntl();
  const locale = useLocale();
  const location = useLocation();
  const {organization} = useParams();
  const [familyHoliday] = useSessionStorage('familyHoliday');

  const constants = useConstants();
  const settings = useOrganizationSettings({organization, familyHoliday});
  const infoTexts = useFieldOptions({organization, prefix, isFormField: false});
  const unions = useUnionTranslator(constants.unions);
  const hideUnableToWork = settings?.hideOptions?.hideUnableToWork;
  const holidayOrg = settings?.organization?.isHolidayOrganization;

  if (hideUnableToWork && constants?.workingStatuses) remove(constants.workingStatuses, v => v === "UNABLE_TO_WORK");
  if (!settings.error) initializeStorage(organization, locale, settings.language);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <ThemeProvider theme={theme => OrganizationTheme({theme, organization})}>
      <HelmetProvider>
        <DataErrorFallback error={constants.error || settings.error || infoTexts.error}>
          <SettingsContext.Provider value={{...constants, ...settings, unions, infoTexts}}>
            <Helmet htmlAttributes={{lang: locale[0]}}>
              <title>LOTU - {intl.formatMessage(holidayOrg ? messages.title : messages.title_c)}</title>
            </Helmet>
            {children}
          </SettingsContext.Provider>
        </DataErrorFallback>
      </HelmetProvider>
    </ThemeProvider>
  );
};

export const getWishArray = () => {
  let wishes = sessionStorage.getItem('wishes');
  if (wishes) wishes = JSON.parse(wishes);
  return wishes || [];
};

export const saveWishArray = (wishes) => {
  if (wishes.length > 0) {
    if (!getPrimary()) savePrimary(wishes[0]);
    sessionStorage.setItem('wishes', JSON.stringify(wishes));
  } else {
    sessionStorage.removeItem('wishes');
    savePrimary();
  }
  return getWishArray();
};

export const getPrimary = () => {
  const primary = sessionStorage.getItem('primary');
  return primary ? JSON.parse(primary) : undefined;
};

export const savePrimary = (primary) => {
  primary ? sessionStorage.setItem('primary', JSON.stringify(primary)) : sessionStorage.removeItem('primary');
  return primary;
};

export const initializeStorage = (org, [locale, setLocale], language) => {
  const cur = sessionStorage.getItem('organization');
  if (!cur || cur !== org) {
    if (language && language.defaultLanguage) setLocale(language.defaultLanguage);
    else setLocale('fi-FI');
    sessionStorage.clear();
    sessionStorage.setItem('organization', org);
    sessionStorage.setItem('instructions', 'open');
  }
};

export const filterModel = (defaults) => ({
  page: defaults?.page ? withDefault(NumberParam, defaults.page) : NumberParam,
  resort: defaults?.resort ? withDefault(NumberParam, defaults.resort) : NumberParam,
  theme: defaults?.theme ? withDefault(NumberParam, defaults.theme) : NumberParam,
  partner: defaults?.partner ? withDefault(NumberParam, defaults.partner) : NumberParam,
  begin: defaults?.begin ? withDefault(StringParam, defaults.begin) : StringParam
});

export const getSessionJSON = (key) => {
  const data = sessionStorage.getItem(key);
  return data ? JSON.parse(data) : false;
};

export const setSessionJSON = (key, object) => {
  sessionStorage.setItem(key, JSON.stringify(object));
};
